import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { SimpleHeaderComponent } from '@ildes/basic/simple-header/simple-header.component';
import { FormatDatePipe } from '@ildes/pipes/format-date.pipe';
import { ApiManagerService } from '@ildes/services/api-manager.service';
import { FormatNumberPipe } from "@ildes/pipes/format-number.pipe";

@Component({
    selector: 'app-bill-detail',
    standalone: true,
    imports: [
        CommonModule,
        SimpleHeaderComponent,
        MatDividerModule,
        FormatDatePipe,
        MatButtonModule,
        MatProgressBarModule,
        FormatNumberPipe,
    ],
    template: `
    <simple-header title="Detalle de la factura" (action)="close()" icon="close"></simple-header>
    <div class="section">
      <h3>Datos de la factura</h3>
      <p class="padding-top-8">
        <strong>Número de precuenta: </strong> {{this.data.number}}
      </p>
      <mat-divider></mat-divider>
      <div class="padding-top-8 padding-bottom-8 flex">
        <div class="fill">
            <strong>Número de factura: </strong> {{this.data.realNumber || 'Pendiente de adjuntar'}}
        </div>
        <div *ngIf="admin">
            <button [disabled]="loadingSetNumber" mat-button (click)="setNumber()" class="button-size-200">
                <span *ngIf="!this.data.realNumber">Adjuntar</span>
                <span *ngIf="this.data.realNumber">Editar</span>
            </button>
        </div>
        <mat-progress-bar mode="indeterminate" *ngIf="loadingSetNumber"></mat-progress-bar>
      </div>
      <mat-divider></mat-divider>
      <p class="padding-top-8">
        <strong>Proyecto: </strong> {{this.data.project.name}}
      </p>
      <mat-divider></mat-divider>
      
      <div class="padding-top-8 padding-bottom-8 flex">
        <div class="fill">
            <strong>Pagado: </strong> {{this.data.payed ? 'Sí' : 'No'}}
        </div>
        <div *ngIf="admin">
            <button [disabled]="loadingPayed" mat-button (click)="togglePayed()" class="button-size-200">
                <span *ngIf="!this.data.payed">Marcar como pagada</span>
                <span *ngIf="this.data.payed">Marcar como no pagada</span>
            </button>
        </div>
        <mat-progress-bar mode="indeterminate" *ngIf="loadingPayed"></mat-progress-bar>
      </div>
      <mat-divider></mat-divider>
      <p class="padding-top-8">
        <strong>Fecha de expedición: </strong> {{this.data.dispatchedDate | formatDate}}
      </p>
      <mat-divider></mat-divider>
      <p class="padding-top-8">
        <strong>Coste: </strong> {{data.cost | appFormatNumber}}
      </p>
      <mat-divider></mat-divider>
      <p class="padding-top-8">
        <strong>Impuestos: </strong> {{this.data.taxes?.amount || 0}}%
      </p>
      <mat-divider></mat-divider>
      <p class="padding-top-8">
        <strong>Coste (+impuestos): </strong> {{this.totalCost | appFormatNumber }}
      </p>
    </div>
    <div class="section margin-top-16">
      <h3>Receptor</h3>
      <p class="padding-top-8">
        <strong>Nombre: </strong> {{this.data.receptor.name}}
      </p>
      <mat-divider></mat-divider>
      <p class="padding-top-8">
        <strong>Apellidos: </strong> {{this.data.receptor.surname}}
      </p>
      <mat-divider></mat-divider>
      
      <p class="padding-top-8">
        <strong>Cédula: </strong> {{this.data.receptor.id}}
      </p>
      <mat-divider></mat-divider>
      <p class="padding-top-8">
        <strong>Dirección: </strong> {{this.data.receptor.address}}
      </p>
      <mat-divider></mat-divider>
      <p class="padding-top-8">
        <strong>Email: </strong> {{this.data.receptor.email}}
      </p>
    </div>

    <div class="section margin-top-16">
      <h3>Conceptos</h3>
      <div *ngFor="let concept of data.concepts">
       <table [width]="size">
            <tr class="header">
              <th>Descripción</th>
              <th>Cantidad</th>
              <th>Observaciones</th>
              <th>Coste por unidad</th>
              <th>Coste</th>
            </tr>
            <tr>
              <td>
                 {{concept.description}}
              </td>
              <td>
                {{concept.amount}}
              </td>
              <td>
                {{concept.observations}}
              </td>
              <td>
                {{concept.costPerUnit | appFormatNumber}}
              </td>
              <td>
                {{concept.cost | appFormatNumber}}
              </td>
            </tr>
        </table>
      </div>
    </div> 
  `,
    styleUrls: ['./bill-detail.component.css'],
})
export class BillDetailComponent {
    public data;
    public admin;
    public loadingPayed;
    public totalCost;
    public loadingSetNumber;

    constructor(
        @Inject(MAT_DIALOG_DATA) data: any,
        public dialogRef: MatDialogRef<BillDetailComponent>,
        private apiManager: ApiManagerService,
    ) {
        this.data = data.bill;
        this.admin = data.admin;
        this.totalCost = { amount: this.data.cost.amount + (this.data.cost.amount * this.data.taxes?.amount || 0), currency: this.data.cost.currency}
    }

    close() {
        this.dialogRef.close(this.data);
    }

    togglePayed() {
        this.loadingPayed = true;
        this.apiManager.modifyBill(this.data.id, {payed: !this.data.payed}).subscribe(() => {
            this.loadingPayed = false;
            this.data.payed = !this.data.payed;
        }, () => {
            this.loadingPayed = false;
        })

    }

    setNumber() {
        const realNumber = window.prompt('Introduzca el número de factura');
        if (!realNumber) {
            return;
        }
        this.loadingSetNumber = true;
        this.apiManager.modifyBill(this.data.id, {realNumber: realNumber}).subscribe(() => {
            this.loadingSetNumber = false;
            this.data.realNumber = realNumber;
        }, () => {
            this.loadingSetNumber = false;
        })
    }
}
